import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * Service responsible for managing HyperFlow Chatbot initialization and lifecycle.
 * 
 * This service provides methods to handle the HyperFlow chatbot
 * with different types of tokens (script tokens and user tokens). It handles the
 * dynamic loading of the chatbot SDK script and manages the chatbot state.
 * 
 * @example
 * ```typescript
 * // Initialize with script token (for non-authenticated users)
 * this.hyperFlowService.initWithScriptToken('your-script-token');
 * 
 * // Initialize with user token (for authenticated users)
 * this.hyperFlowService.initWithUserToken('user-session-token');
 * ```
 * 
 * @author Your Team
 * @since 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class HyperFlowChatbotService {

  /**
   * Control flag to track if the HyperFlow chatbot has been initialized.
   * Prevents multiple initializations and enables proper reinitializations.
   * 
   * @private
   * @type {boolean}
   */
  private initialized: boolean = false;

  /**
   * Initializes the HyperFlow chatbot with a script token.
   * 
   * This method is typically used for anonymous users. If the chatbot
   * is already initialized, it will reinitialize with the new script token.
   * The method dynamically loads the HyperFlow SDK script and initializes the
   * chatbot with the provided token.
   * 
   * @param {string} token - The script token used to initialize the chatbot, this token is provided by hyperflow in websdk panel
   * 
   * @example
   * ```typescript
   * // Initialize chatbot for anonymous users
   * this.hyperFlowService.initWithScriptToken('abc123-script-token');
   * ```
   * 
   * @see {@link reinitWithScriptToken} - Called internally when already initialized
   * @since 1.0.0
   */
  initWithScriptToken(token: string): void {
    // Safe check to avoid multiple inits
    if (this.initialized) {
      this.reinitWithScriptToken(token);
      return;
    }

    const script = document.createElement("script");
    script.src = environment.urlChatbotSdk;
    script.async = true;
    script.onload = () => {
      (window as any).Hyperflow.init(token);
      this.initialized = true;
    };

    document.head.appendChild(script);
  }

  /**
   * Reinitializes the HyperFlow chatbot with a new script token.
   * 
   * This private method is called internally when the chatbot is already initialized
   * and needs to be reinitialized with a different script token. It loads a new
   * instance of the SDK script and calls the reinit method from the HyperFlow API.
   * 
   * @private
   * @param {string} token - The new script token for reinitializing the chatbot
   * 
   * @warning This method adds a new script element to the DOM each time it's called,
   * which may lead to memory leaks or script conflicts in long-running applications.
   * 
   * @since 1.0.0
   */
  private reinitWithScriptToken(token: string): void {
    const script = document.createElement("script");
    script.src = environment.urlChatbotSdk;
    script.async = true;
    script.onload = () => {
      const hyperFlow = (window as any).Hyperflow;
      hyperFlow.reinit(token);
      this.initialized = true;
    };

    document.head.appendChild(script);
  }

  /**
   * Initializes the HyperFlow chatbot with a user token for authenticated users.
   * 
   * This method is used for authenticated users and provides personalized chatbot
   * experience. It uses the environment chatbot token as the main token and passes
   * the user token as the sender identifier. If already initialized, it will
   * reinitialize with the new user context.
   * 
   * @param {string} token - The user session token or identifier
   * 
   * @example
   * ```typescript
   * // Initialize chatbot for authenticated user
   * this.hyperFlowService.initWithUserToken('user-session-12345');
   * 
   * // After user login
   * this.authService.login().subscribe(user => {
   *   this.hyperFlowService.initWithUserToken(user.sessionToken);
   * });
   * ```
   * 
   * @see {@link reinitWithUserToken} - Called internally when already initialized
   * @since 1.0.0
   */
  initWithUserToken(token: string): void {
    // Safe check to avoid multiple inits
    if (this.initialized) {
      this.reinitWithUserToken(token);
      return;
    }

    const script = document.createElement("script");
    script.src = environment.urlChatbotSdk;
    script.async = true;
    script.onload = () => {
      const hyperFlow = (window as any).Hyperflow;
      hyperFlow.init(environment.chatbotToken, { sender: token });
      this.initialized = true;
    };

    document.head.appendChild(script);
  }

  /**
   * Reinitializes the HyperFlow chatbot with a new user token.
   * 
   * This private method handles the reinitialização process for user tokens.
   * It maintains the environment chatbot token while updating the user context
   * through the sender parameter.
   * 
   * @private
   * @param {string} token - The new user token for reinitializing the chatbot
   * 
   * @example
   * ```typescript
   * // Called internally when switching between users
   * this.reinitWithUserToken('new-user-session-67890');
   * ```
   * 
   * @warning Like reinitWithScriptToken, this method also adds new script elements
   * to the DOM which may cause memory issues.
   * 
   * @since 1.0.0
   */
  private reinitWithUserToken(token: string): void {
    const script = document.createElement("script");
    script.src = environment.urlChatbotSdk;
    script.async = true;
    script.onload = () => {
      (window as any).Hyperflow.reinit(environment.chatbotToken, { sender: token });
      this.initialized = true;
    };

    document.head.appendChild(script);
  }

  /**
   * Checks if the HyperFlow chatbot is currently initialized.
   * 
   * @returns {boolean} True if the chatbot is initialized, false otherwise
   * 
   * @example
   * ```typescript
   * if (this.hyperFlowService.isInitialized()) {
   *   console.log('Chatbot is ready to use');
   * }
   * ```
   * 
   * @since 1.0.0
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}
